import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { Box, Collapse, Skeleton, Stack, useMediaQuery } from '@mui/material'

import { DEFAULT_COMPANY_ID } from 'config'
// The rail's height is a viewport `calc` that tracks the text-zoom preference, like every
// other full-height surface in the app.
import { vhMinus, zoomOf } from 'utils/textZoom'
// Shared rather than a fifth copy — see the util's own note on §36 and on why it must not
// gate on `scrollHeight > clientHeight`.
import { getScrollParent } from 'utils/scrollParent'
import { useSelector } from 'store/hooks'
import { useAnalyticsRoi, useAnalyticsTotals, useCompany } from 'hooks/api'
import { AnalyticsBlock, Header, IntroDataBanner } from 'components'
import { EM_DASH_PLACEHOLDER } from 'components/display/Analytics/Analytics'
import { useSnackbar } from 'contexts'
import { DEFAULT_REPORTING_CURRENCY, type TopInfluencerDimension } from '@repo/types'

import type { FilterOpenRequest } from 'components/input/ActionLine'
import {
  ALL_ROI_CARDS,
  ANALYTICS_TILE_IDS,
  AnalyticsAppliedFilters,
  AnalyticsCanvas,
  AnalyticsCustomizeButton,
  AnalyticsCustomizePanel,
  PANEL_VIEWPORT_RESERVE_PX,
  AnalyticsFiltersHeader,
  AnalyticsViews,
  MetricInputsNeededButton,
  RoiHero,
  downloadAnalyticsExport,
  parseAnalyticsVariantCardId,
  useAnalyticsView,
  useRoiCards,
  type AnalyticsCardItem,
} from 'components/analytics'
// The panel components themselves are no longer referenced here (VRG-481): the two
// standalone panel cards are gone, and the variants that replaced them are built in
// `analyticsDisplayVariants`, which renders them.
import {
  CARD_MIN_HEIGHT,
  DEFAULT_TOP_INFLUENCER_DIMENSION,
  OVERVIEW_STAT_BOXES,
  OverviewStat,
  type ScansMapMode,
} from './components'
// VRG-467 — the chart / map variants of a metric. Beside the page rather than in
// `components/analytics` because it renders `ScansByTime2` / `ScansByLocation`, which
// live under this page; a shared module reaching back into a page's components would
// invert the dependency.
import {
  analyticsDisplayVariantIds,
  buildAnalyticsDisplayVariantCards,
  buildComposedDisplayVariantCard,
  type DisplayVariantSource,
} from './analyticsDisplayVariants'

const MS_MINUTE = 60 * 1000
const MS_HOUR = 60 * MS_MINUTE
const MS_DAY = 24 * MS_HOUR

/**
 * ── THE STICKY HEADER BLOCK ────────────────────────────────────────────────────────
 *
 * ## WHAT STICKS: the page title (with the Ask Vera launcher on its row) AND the control
 * band. NOT the hero, which scrolls BETWEEN them.
 *
 * 🚨 REVISED THREE TIMES, and the title has now come back. The history is worth keeping
 * because the reasoning that removed it was sound and was overruled on a point it never
 * addressed. It originally held the title on a "title / controls / toolbar are one visual
 * unit" argument; that died when the ROI hero moved BETWEEN the title and the controls,
 * since they were no longer adjacent and there was no unit left to split. It then held TWO
 * rows — a date-picker row above the action line — and that is gone too: the date range
 * moved INTO the action line (`AnalyticsFiltersHeader.dateRangeControl`).
 *
 * The title is back by user directive ("sticky header should still show Analytics and the
 * VERA ask button"), and the adjacency objection does not survive the arrangement that
 * satisfies it. THERE ARE TWO STICKY BOXES, NOT ONE: the title pins at `top: 0`, the control
 * band pins at `top: <measured title height>`, and the hero scrolls up UNDER the title band
 * in between. Once anything has scrolled, the two bands are flush and read as one block —
 * which is the unit the original argument wanted — while at rest the hero still sits between
 * them, which is what keeps the "the controls below do not scope the hero" statement true
 * (see the hero's own note). Neither box moves the other: the offsets are measured, not
 * assumed.
 *
 * What sticks is decided on its own merits:
 *
 *  1. **The controls are the interpretive frame, not decoration.** A scan count is
 *     meaningless without the period and the dimensions it was measured over. Scrolling a
 *     filtered dashboard away from the controls that filtered it is how a reader ends up
 *     reading a narrowed figure as a total. The date range IS that frame — which is also
 *     why moving it into this block rather than out of it was the right direction — and so
 *     is Filter's applied-count badge beside it.
 *  2. **The action line owns Customize, and Customize opens a STICKY panel.** A pinned
 *     side panel whose opener has scrolled out of view is incoherent — and worse, the
 *     panel is a drag source, so its whole reason for being sticky is that a gesture
 *     which scrolls must not strand the user. Half of that pair pinning and half not is
 *     the arrangement neither state wants.
 *  3. **The title's row carries global chrome, not just a name.** `Header` renders the Ask
 *     Vera launcher on every non-nested page (see `AskVeraLauncher`), so pinning this row
 *     keeps the assistant reachable from anywhere on a page that is ~2000px of dashboard.
 *     That is the half of the directive that does real work; the word "Analytics" is the
 *     cheap half. It also costs the maximise path nothing and helps it — the title used to
 *     be the FIRST thing to leave when a stray scroll moved a maximised card (see the
 *     rail's height note), and it can no longer leave at all.
 *
 * ⚠️ This is a deliberate exception to "a title that scrolls is what every other page in
 * this app does", which is what removed it last time. `Header` is not sticky anywhere else
 * and should not become so by default — the justification here is the dashboard's length
 * plus the launcher on its row, and it does not generalise to a table page.
 *
 * ## THE HERO EXPLICITLY DOES NOT STICK, and that is a decision
 *
 * It is ~170px tall — three figures at 56/36/36px, a scope-and-caveat row and a disclosure
 * control. Pinned on top of the two bands it would hold ~354px, well over a third of an
 * 800px viewport, permanently reserved for a card the reader has already read.
 *
 * The hero is a HEADLINE: read once on arrival, not consulted while scanning the cards
 * below. The controls are the opposite — consulted continuously, and cheap. So the hero
 * scrolls away like content and only the title and the controls pin.
 *
 * ## THE BAND'S COST, MEASURED
 *
 * Measured on /analytics at 1280×800, where the toolbar wraps to two lines (it does at this
 * width — left group 775px + right group 504px against 1152px of content box):
 *
 *   ~211px   title + a date-picker row + the action line   (the first arrangement)
 *    143px   date row + action line, no title
 *    127px   action line alone, date picker folded into it
 *    183px   title + action line — 56px title band + 127px control band  ← NOW
 *
 * 183.5 of an 800px viewport is 22.9%. The line this file has drawn since the hero
 * discussion is "roughly a third" (267px), so this clears it with ~83px to spare, and it is
 * still 27px BELOW where the page started. At a width where the toolbar does not wrap the
 * control band is 66px and the total is ~122px (15%).
 *
 * ## THE TOP OFFSET IS 0, AND 0 IS NOT THE WINDOW EDGE
 *
 * The page scrolls an INNER div (`Layout`'s content container — `window.scrollY` stays 0
 * while that element's `scrollTop` moves), and that container carries `padding: 24px`, so
 * a sticky offset of `0` comes to rest 24px below the window. Measured, not assumed;
 * `AnalyticsCustomize`'s `PANEL_VIEWPORT_RESERVE_PX` records the same arithmetic from the
 * other side, including the case that proves it (`top: 24px` pins at 48).
 *
 * That 24px band above the pinned block is inside the scrollport, so content scrolls
 * THROUGH it. `STICKY_BLEED_PX` covers it with a pseudo-element rather than a negative
 * margin: an out-of-flow box paints the band without moving the header, so the title's
 * position on the sidebar-logo datum (§29) is untouched — which a margin would not have
 * been able to promise.
 *
 * ⚠️ `STICKY_TOP_PX` is the TITLE box's offset now. The control band's offset is the title
 * band's MEASURED height, so the two are flush at every viewport and font size without a
 * second constant to keep in sync. Do not replace that measurement with a literal.
 */
const STICKY_TOP_PX = 0

/**
 * How far the sticky block's background extends ABOVE its own box, in px.
 *
 * ── 24, AND IT IS PINNED FROM BOTH SIDES. IT IS NOT A SLACK VALUE ─────────────────────
 *
 * 🚨 THIS WAS 48, AND THE 24 OF "PROVABLY FREE" OVERSHOOT WAS THE ROI-HERO CLIPPING BUG.
 * That is worth spelling out, because the reasoning it replaces was carefully argued and
 * still wrong, and because the defect survived six measurements that all looked in the
 * wrong place.
 *
 * The old claim was that vertical overshoot costs nothing: pinned, the excess is clipped by
 * the window; at rest, it "paints over the page's own background in the same colour". The
 * first half is true. The second half was true only while the element ABOVE this block was
 * the page title — bare page background, so painting page background over it is a no-op.
 * It stopped being true the moment the ROI HERO moved between the title and the controls,
 * and nothing re-derived it. The hero is a `Paper`: `background.paper` (#0F0F12), not
 * `background.default` (#09090B). The band paints the DEFAULT colour, so its overshoot no
 * longer repainted the same colour — it painted the page over the bottom of a card.
 *
 * Measured at 1280×800 before the fix, at every scroll offset from 0 until the card leaves
 * the viewport: hero card bottom 569.7, sticky box top 593.7, so the band spanned 545.7 →
 * 593.7 and **overlapped the card by exactly 24px**. `document.elementFromPoint(600, 550)`
 * returned the sticky block, not the card. The overlap is CONSTANT, because the hero's
 * `mb: 6` (24px) is the only thing between them and the two scroll together — which is why
 * this was never a scroll-position bug and why looking for one at scroll-top found nothing.
 *
 * ⚠️ WHY EVERY EARLIER MEASUREMENT SAID "NOT CLIPPED", AND WHAT TO MEASURE INSTEAD. The
 * card's own geometry is innocent: `overflow: visible`, `scrollHeight === clientHeight`, no
 * clipping ancestor, and ~25px of clearance below the chart's x-axis labels inside its own
 * rect. All true, all irrelevant. Nothing was CLIPPING the card; something was PAINTING
 * OVER it. A rect does not record who covers it, so `getBoundingClientRect` can never see
 * this. `elementFromPoint` at the card's bottom edge can, and does, in one call.
 *
 * ── THE TWO BOUNDS ────────────────────────────────────────────────────────────────────
 *
 *   ≥ 24  or the band is short. `Layout`'s scroll container carries `padding: 24px` and
 *         `STICKY_TOP_PX` is 0, so the block comes to rest 24px below the window (see
 *         above). That 24px strip is inside the scrollport, so content scrolls THROUGH it
 *         unless this covers it.
 *   ≤ 24  or the band eats the hero. The gap above this block is the hero wrapper's
 *         `mb: 6` — 24px — and this is an opaque box painted in a stacking context at
 *         `STICKY_Z_INDEX`, so every px past the gap lands on top of the card.
 *
 * Both bounds are the same 24 and the value is therefore exact, not chosen. They are the
 * same number for a reason rather than by luck — both are `theme.spacing(6)` — but they are
 * DIFFERENT quantities, so if either moves this has to be re-derived rather than assumed
 * still correct. Raising it "for safety" is precisely the change that caused this bug.
 *
 * ── WHICH ELEMENT CARRIES IT MOVED; THE VALUE DID NOT ─────────────────────────────────
 *
 * The bleed now hangs off the TITLE box, which is the topmost pinned element since the title
 * came back into the pinned chrome. The control band no longer draws one, and that is a
 * removal rather than an oversight: the 24px strip above the control band is the BOTTOM of
 * the title band, which is already opaque, so a second bleed there would have painted
 * `background.default` over the last 24px of the title box — measured, that is window rows
 * 56.5→80.5 against a title whose text box ends at 59.6, i.e. it would have clipped ~3px off
 * the bottom of the word "Analytics". Exactly the failure mode this constant was cut from 48
 * to 24 to stop, one element further up.
 *
 * The two bounds still hold for the title box, one of them now trivially: `≥ 24` is
 * unchanged (it is `Layout`'s padding, and this box pins at `top: 0` like the old one did),
 * and `≤ 24` is slack because there is nothing above the title to paint over — it is the
 * page's first element. Slack, NOT free: if anything is ever rendered above the title, that
 * bound becomes live again and this has to be re-derived.
 *
 * ⚠️ Overshoot on the HORIZONTAL axis is likewise not free — see the `left`/`right` bleed
 * below, which had to move from -48 to -24 for the unrelated reason that there the excess
 * becomes real overflow, silently clipped by `Layout`'s `overflowX: hidden`.
 */
const STICKY_BLEED_PX = 24

/**
 * ── THE GAP BELOW THE ACTION LINE IS `ActionLine`'s OWN, AND NOTHING IS ADDED TO IT ──────
 *
 * Both are ZERO, and that is the point: the distance from the search field to the first card
 * is the shared `ActionLine`'s `pb: 16px` and nothing else, which is exactly what every table
 * page renders.
 *
 * They were 12 and 12, for a deliberate 24px that made Analytics the outlier. Measured at
 * 1280×800, search-bottom to first-content-top:
 *
 *   /certificates ....... 16px    grid top == action line bottom, zero added
 *   /analytics (before) . 40px    = 16 (ActionLine pb) + 12 (this) + 12 (canvas mt)
 *   /analytics (after) .. 16px
 *
 * ## Why the 12px of sticky padding was redundant, not merely too much
 *
 * Its stated job was to give the pinned band breathing room under the action line "once
 * content is scrolling beneath it" — without it, the reasoning went, a scrolled card would
 * touch the action line's baseline. That is not what the DOM does: `ActionLine`'s own
 * `pb: 16px` is INSIDE this sticky block, so the band already extends 16px below the search
 * field's bottom edge. Measured before the change — search field bottom 207.5, action line
 * bottom 223.5, sticky block bottom 235.5: the band ran 28px below the controls, of which 16
 * came free. Removing this padding leaves those 16, which is precisely the clearance a table
 * page's own sticky toolbar has. The case it defended against was already covered.
 *
 * Kept as named zeros rather than deleted, so the two contributions stay visible at the site
 * that would otherwise silently re-add them. If a real gap is ever wanted here it belongs in
 * `ActionLine`, where every page gets it, not on this page alone.
 */
const STICKY_PAD_BOTTOM_PX = 0
const CANVAS_TOP_GAP_PX = 0

/**
 * Above the grid, below the date picker's popper.
 *
 * `react-grid-layout` gives a dragged item `z-index: 3` and its drop placeholder `2`, so a
 * card dragged upward would otherwise be painted over the pinned header. The picker's
 * popper is at 1400 and must stay above everything here — it is opened FROM this block.
 */
const STICKY_Z_INDEX = 4

/** The panel's slide, in ms. Also the settle timer — see `AnalyticsCustomizePanel`. */
const CUSTOMIZE_SLIDE_MS = 260

/**
 * A NOUN PHRASE naming the LENGTH of the comparison window — "30 days", "week",
 * "3 months" — because `AnalyticsBlock` renders it as "vs. previous {interval}" and
 * "none in the previous {interval}".
 *
 * Derived from `previous_range`, the window the SERVER actually measured, and never
 * from the date picker or a constant. Those can disagree with it: the picker holds the
 * CURRENT window and the API resolves an open-ended `to` to "now" itself, so a label
 * taken from the client can name a period the delta was not computed over. A delta
 * labelled with the wrong period is worse than no delta — it is a wrong number that
 * looks right, and on this product that is the expensive class of mistake.
 *
 * Only EXACT matches get a name. A calendar-month phrase is used solely when adding
 * that many months to `from` lands precisely on `to`, so "3 months" can never stand in
 * for "roughly 90 days"; a whole number of days is reported as days; anything else
 * degrades to the uninformative-but-never-wrong "period" rather than to a naked
 * percentage. `previous_range.to` is the EXCLUSIVE bound, so `to - from` is the
 * window's true length with no off-by-one.
 */
export function describePreviousRange(range?: { from: string; to: string }): string | undefined {
  if (!range) return undefined

  const from = new Date(range.from)
  const to = new Date(range.to)
  const ms = to.getTime() - from.getTime()

  // An unparseable or inverted range still gets a comparison phrase, just no length.
  if (!Number.isFinite(ms) || ms <= 0) return 'period'

  // Calendar months first — a month is not a fixed number of days, so an exact
  // round-trip is the only way to say "month" without approximating one. The equality
  // check is what makes it safe: `setMonth` OVERFLOWS rather than clamping (Jan 31 + 1
  // month lands in March), and an overflowed probe simply fails and falls through to
  // days. Given the API shifts by elapsed milliseconds, this path fires rarely; that is
  // the correct outcome, not a gap — "31 days" is what was measured.
  const months = (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth())
  if (months >= 1) {
    const probe = new Date(from)
    probe.setMonth(probe.getMonth() + months)
    if (probe.getTime() === to.getTime()) return months === 1 ? 'month' : `${months} months`
  }

  // Whole days, with one hour of tolerance so a DST transition inside the window does
  // not turn 30 calendar days into "29.96". The tolerance absorbs a clock shift and
  // nothing else — it is two orders of magnitude too small to round one day count into
  // another, so it cannot silently mislabel a window.
  const wholeDays = Math.round(ms / MS_DAY)
  if (wholeDays >= 1 && Math.abs(ms - wholeDays * MS_DAY) <= MS_HOUR) {
    if (wholeDays === 1) return 'day'
    if (wholeDays === 7) return 'week'
    return `${wholeDays} days`
  }

  const wholeHours = Math.round(ms / MS_HOUR)
  if (wholeHours >= 1 && Math.abs(ms - wholeHours * MS_HOUR) <= MS_MINUTE) {
    return wholeHours === 1 ? 'hour' : `${wholeHours} hours`
  }

  return 'period'
}

/*
  ⚠️ THE PAGE-LOCAL `downloadCSV` IS GONE (VERA-260). It wrote ONE file holding the four
  KPI tiles, hardcoded, with no header saying what they were filtered to — so a canvas of
  a dozen cards exported four numbers, and those four could not be reconciled with the
  screen by anyone who did not already know the filters. Its escape function was also its
  own third copy of a two-line routine.

  The export now lives in `components/analytics/analyticsExport.ts`: one CSV per card on
  the canvas, each carrying a provenance block, delivered as a zip. See that file's header
  for why one-file-per-card and why the provenance is the load-bearing half.
*/

/**
 * One line per KPI, for the card library's list.
 *
 * Written here rather than on `OverviewStatBox` because it describes the CARD ("what
 * you get if you add this"), not the metric. The metric's own definition already has
 * a single home — the glossary, reached through the card's `term` (§44) — and a second
 * copy of it in this file would be exactly the drift that rule exists to prevent.
 */
const KPI_SUMMARIES: Record<string, string> = {
  scans_count: 'Total scans recorded in the period.',
  certificates_count: 'Certificates in existence at the end of the period.',
  completed_transfers_count: 'Ownership transfers that finished in the period.',
  in_progress_transfers_count: 'Transfers started and not yet completed.',
}

/**
 * An element's live height in px, plus the ref to attach.
 *
 * Extracted at the SECOND use (§36): the pinned control block needed it so the Customize
 * panel could pin below rather than under it, and the ROI hero band needs it so the canvas
 * knows how much chrome disappears when a card expands. Two copies of this would be two
 * places to get the measurement schedule wrong.
 *
 * ## Why `useLayoutEffect` with NO dependency array
 *
 * It runs after every commit, which is the only schedule that cannot go stale — a height
 * changes as a function of the rendered subtree, not of any one state value, and
 * enumerating the inputs is how this kind of measurement rots. The cost is one
 * `getBoundingClientRect` per render, and the tolerance guard means a stable height sets no
 * state, so there is no loop. `useLayoutEffect` rather than `useEffect` so a consumer is
 * never painted one frame at the wrong offset.
 *
 * ## Why `ResizeObserver` is ADDITIONAL rather than instead
 *
 * It is the more direct instrument and is the only thing that catches a height change with
 * no React commit behind it — a font load, a container width change from the sidebar
 * collapsing. But it fires ZERO times in the Claude preview pane, so it cannot be the only
 * mechanism or none of this is verifiable there. Same reasoning, and the same pairing, as
 * `AnalyticsCanvas`'s own width measurement.
 *
 * The observer binds once, so a node that mounts LATER (the hero, which is conditional) is
 * never observed. That is covered rather than overlooked: the every-commit measurement
 * above is what actually tracks a conditional node, and the observer is the extra that
 * catches commit-less changes on a node that was there at bind time.
 *
 * ## ⚠️ A DETACHED NODE KEEPS THE LAST MEASUREMENT
 *
 * `if (!el) return` rather than reporting 0, and this is load-bearing rather than
 * defensive. The hero unmounts in the same commit that expands a card, and the canvas needs
 * the height it HAD in order to place the expanded region; a hook that zeroed on unmount
 * would supply 0 at exactly the moment the number is used. The last known height is the
 * honest answer to "how much space did this take", and it is also the value it will have
 * again when it comes back.
 */
const useMeasuredHeight = (): [React.RefObject<HTMLDivElement>, number] => {
  const ref = useRef<HTMLDivElement>(null)
  const [height, setHeight] = useState(0)

  const measure = useCallback(() => {
    const el = ref.current
    if (!el) return
    // LAYOUT px (painted height ÷ the element's zoom), because every consumer writes this
    // straight back into a style inside `#app`'s text zoom — sticky `top`, `scrollMarginTop`,
    // a `vhMinus` offset, the canvas's `collapsingChromeAbovePx` — where it is scaled by z
    // again. Painted px here pinned the card toolbar (z − 1) × the title band away from
    // the band it sits under: overlapping it at z < 1, leaving a gap at z > 1.
    const next = el.getBoundingClientRect().height / zoomOf(el)
    setHeight(prev => (Math.abs(prev - next) > 0.5 ? next : prev))
  }, [])

  useLayoutEffect(measure)

  useLayoutEffect(() => {
    window.addEventListener('resize', measure)
    const observer =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null
    if (observer && ref.current) observer.observe(ref.current)
    return () => {
      window.removeEventListener('resize', measure)
      observer?.disconnect()
    }
  }, [measure])

  return [ref, height]
}

export const AnalyticsPage = () => {
  const filters = useSelector(state => state.analytics.filters)

  /**
   * ── THE PRIOR-PERIOD COMPARISON IS SUPPRESSED WHEN IT CANNOT BE A MEASUREMENT ──────
   *
   * 🚨 THE FAILURE IS SILENT AND IT FLATTERS. The server's comparison window is
   * `[from − length, from)` (`previousWindow()`), and it is not clamped to anything. Ask
   * for a range whose preceding window sits before the brand existed and every metric is
   * compared against a GUARANTEED ZERO — at which point `AnalyticsBlock` renders
   * growth-from-nothing as a green "New" and the whole KPI row reads as good news, on
   * every tile at once, for a brand that may be flat or falling. It is not a measurement;
   * it is an artefact of the range, and it produces a plausible number rather than an
   * error, which is why it needs a gate rather than a note.
   *
   * The test is that the PREVIOUS window lies ENTIRELY within the company's lifetime —
   * `from − length >= created_at` — not merely that it overlaps. A partial overlap is the
   * same defect in milder form: the prior period is short by however much of it predates
   * the brand, so the denominator is understated and the delta is inflated by
   * construction, in the flattering direction again.
   *
   * ⚠️ THIS IS A LIVE DEFECT, not a precaution for the hero's new "All time" option. The
   * hero owns its own period and never fed this request. What can reach it is the PAGE's
   * picker, and it does so routinely: a hand-picked range starting near signup, or — as on
   * this fixture — the 30-day bootstrap on a brand created this week. "All time" on the
   * page picker (`DateRangeShortcutOptions.includeAllTime`, still off) is only the extreme
   * case, and this gate is what its own note lists as the first of two blockers.
   *
   * Suppression means OMITTING the flag, so the server returns no `previous` at all and
   * the tiles render no delta — absent, not zero. The alternative, showing a zeroed
   * comparison, asserts the very thing that is unknown.
   *
   * ⚠️ Client-side deliberately. The honest fix is server-side — the API knows the
   * company's `created_at` and could clamp or refuse the window itself, which would cover
   * every caller rather than this one. That means changing `analytics.service.ts` /
   * `previousWindow`, which is being actively rewritten under VRG-459 right now, so it is
   * done here to avoid colliding mid-flight. TODO(VRG-459-Platform.Analytics.Brand.Inputs):
   * move the clamp behind `compare_to_previous` server-side and delete this gate.
   */
  const { data: company } = useCompany({ id: DEFAULT_COMPANY_ID })

  const previousWindowIsMeasurable = useMemo(() => {
    // No creation date yet (still loading, or the request failed) — keep the comparison.
    // The gate exists to remove a KNOWN artefact, and suppressing deltas on every load
    // where one lookup is slow would be a worse default than the artefact it prevents.
    if (!company?.created_at) return true
    const from = filters.dateRange.from.getTime()
    const length = filters.dateRange.to.getTime() - from
    return from - length >= new Date(company.created_at).getTime()
  }, [company?.created_at, filters.dateRange.from, filters.dateRange.to])

  const { data: totalsData, isLoading: totalsLoading } = useAnalyticsTotals({
    from: filters.dateRange.from,
    to: filters.dateRange.to,
    company_ids: [DEFAULT_COMPANY_ID],
    certificate_ids: filters.certificateIds.length > 0 ? filters.certificateIds : undefined,
    owner_ids: filters.ownerIds.length > 0 ? filters.ownerIds : undefined,
    product_ids: filters.productIds.length > 0 ? filters.productIds : undefined,
    location: filters.location.length > 0 ? filters.location : undefined,
    // The KPI row renders a trend delta, so it needs the immediately preceding window.
    // It rides on THIS request rather than a second one: `useAnalyticsTotals` reads a
    // single redux slot the reducer overwrites wholesale, so two concurrent fetches with
    // different ranges would race into it and both consumers would read the winner.
    //
    // Conditional — see `previousWindowIsMeasurable`. Omitted rather than sent-and-ignored
    // so the server returns no `previous` key and the tiles show no delta at all; a zeroed
    // comparison would read as "grew from nothing", which is the artefact being removed.
    compare_to_previous: previousWindowIsMeasurable,
  })

  /**
   * The ROI METRIC CARDS' data, over the page's date range.
   *
   * ⚠️ NOT the hero's any more. The hero owns its own period and issues its own request
   * into a separate slot (`RoiHero` / `Actions.GET_ANALYTICS_ROI_HERO`) — two windows,
   * two requests, because one redux slot shared between them is a last-writer-wins race.
   *
   * This response still carries a `roi` summary, and two things on the page read it, both
   * for the BILLING BOUNDARIES rather than for the figures: `AnalyticsFiltersHeader`
   * bootstraps the page's default range from them, and `RoiHero` resolves its own default
   * period from them. `getVeraSpend` returns the very `current_period_start` /
   * `current_period_end` it compared `exact` against, which is why both prefer this copy
   * over the subscription endpoint's.
   *
   * Company-scoped for the same reason as before: the API returns `roi` only when the
   * request names a single company, and omits it otherwise rather than summing bills
   * across brands.
   */
  const { data: roiData, isLoading: roiLoading, refetch: refetchRoi } = useAnalyticsRoi({
    from: filters.dateRange.from,
    to: filters.dateRange.to,
    company_ids: [DEFAULT_COMPANY_ID],
    certificate_ids: filters.certificateIds.length > 0 ? filters.certificateIds : undefined,
    owner_ids: filters.ownerIds.length > 0 ? filters.ownerIds : undefined,
    product_ids: filters.productIds.length > 0 ? filters.productIds : undefined,
    location: filters.location.length > 0 ? filters.location : undefined,
  })

  /**
   * ── THE SKELETON IS FOR THE FIRST LOAD ONLY (§19) ──────────────────────────────────
   *
   * `useQuerySelect` sets `isLoading = true` on EVERY params change, not just the first
   * (`hooks/useQuerySelect.ts:37` — it serialises the params and flips the flag whenever
   * they differ). So changing any filter made every consumer that reads `isLoading`
   * directly swap its content for a grey block and back: the whole KPI row, all thirteen
   * ROI cards and the hero UNMOUNTED and were rebuilt from nothing, ~200ms later. That is
   * the "all cards flash" report, and it is also the "page moves up a little" one — the
   * hero's skeleton is a different height from the loaded hero (no caveat line), so
   * everything below it shifted twice per filter change.
   *
   * The hero now applies the same rule from inside itself (`RoiHero` gates on `!roi`,
   * since its `isLoading` is additionally FALSE while its request is disabled waiting for
   * the billing boundaries); the two flags below cover the KPI tiles and the ROI cards.
   *
   * The test is whether there is anything on screen to keep, and the honest form of that
   * is whether the RESPONSE has ever landed: `totalsData` / `roiData` are redux slots the
   * reducer only ever writes on success and never clears, so `!data` is true exactly
   * once — before the first response. After that a refetch keeps the previous numbers
   * visible and replaces them in place, which is what §19 asks for.
   *
   * ⚠️ Fixed HERE, at the consumers, and deliberately not inside `useQuerySelect`. That
   * hook is used by every list and table in the platform; changing what its `isLoading`
   * means would change all of them at once, from a page that only had evidence about
   * this one. `ScansByTime2` already carries the same fix in its own terms
   * (`isLoading && chartData.length === 0`).
   */
  const totalsFirstLoad = totalsLoading && !totalsData
  const roiFirstLoad = roiLoading && !roiData


  // The ROI card VALUES, injected from the single request above. `useRoiCards` takes
  // the response rather than fetching its own — see its header for why that is a
  // correctness fix and not a tidy-up (two differently-scoped requests raced into one
  // redux slot, so the hero's denominator could be measured across every company).
  // `.data` (the per-metric `IRoiMetrics`), NOT `.roi` (the hero's `IRoiSummary`).
  // One response carries both: the cards read the individual metrics, the hero reads
  // the single multiple computed over them.
  // `.data` is the per-metric `IRoiMetrics`; `.roi` is the hero's `IRoiSummary`. One
  // response carries both, and the registry-backed cards need the SUMMARY — it is the
  // only half that says which metrics are withheld and why (VRG-459).
  // The 4th arg is `onSaved`. WITHOUT it, saving a figure from a card's assumption marker
  // persists to the server but the card keeps showing the OLD number until a full reload —
  // the save silently appears not to have worked.
  //
  // ⚠️ It must be passed HERE, from the page, and NOT re-dispatched from inside the hook.
  // The ROI response lives in a redux slot keyed on this page's filters; a dispatch that
  // does not carry them is the cross-tenant scoping hazard this file's header documents
  // (two differently-scoped requests racing into one slot, so the hero's denominator could
  // be measured across every company). `MetricInputsNeededButton` already gets exactly this
  // callback at its own call site below.
  const { byId: roiCardsById } = useRoiCards(
    roiData?.data,
    roiLoading,
    roiData?.roi,
    () => refetchRoi(),
  )

  /**
   * The map's all/latest mode.
   *
   * Lifted to the page because the control that sets it renders in the card shell's
   * HEADER while the map it drives renders in the shell's BODY — two subtrees that
   * meet here and nowhere lower.
   */
  const [scansMapMode, setScansMapMode] = useState<ScansMapMode>('all')

  /**
   * The leaderboard's grouping axis (VERA-260) — owners / products / countries / cities.
   *
   * Lifted for exactly the same reason as `scansMapMode` directly above, and page-level
   * for the same reason too: only `scans_count` is table-capable today, so at most one
   * leaderboard card exists and there is no second card for a shared value to be wrong
   * for. See `DisplayVariantContext` for when that stops being true.
   */
  const [topInfluencerDimension, setTopInfluencerDimension] =
    useState<TopInfluencerDimension>(DEFAULT_TOP_INFLUENCER_DIMENSION)

  /** Which card, if any, owns the whole content region. Ephemeral — never saved. */
  const [expandedId, setExpandedId] = useState<string | null>(null)

  /** Is the card library docked open? Also ephemeral — a session choice, not a view. */
  const [customizeOpen, setCustomizeOpen] = useState(false)

  /**
   * ── 🚨 OPENING THE LIBRARY SCROLLS IT INTO ITS PINNED SLOT ─────────────────────────
   *
   * ## The reported bug, measured
   *
   * At 1280x800, scroll 0, panel open: the rail measured **top 720.7, bottom 1289.2**
   * against an 800px viewport — 489px of a 568.5px panel below the fold, with **79.3px
   * visible**. That 79px is the header and the top rounded corner, which is exactly the
   * screenshot: a panel that appears to be sawn off at the bottom of the page.
   *
   * ## It is NOT a stale height term, and that is worth pinning down
   *
   * The obvious suspect is the rail's `height` `calc`, since `pinnedChromeHeight` grew
   * from 127 to 183.5 when the title became a second sticky box. Measured, that
   * expression is exactly right: `800 - (48 + 183.5) = 568.5`, and at the rail's pinned
   * rest the panel sits **207.7 -> 776.2**, i.e. flush under the pinned chrome and 23.8px
   * above the window bottom — the page gutter, which is where a card on the canvas ends
   * too. Nothing about the arithmetic is wrong.
   *
   * The cause is WHERE THE RAIL STARTS. It is a flex sibling of the canvas, so its
   * natural flow position is below the title band, the ~430px ROI hero and the control
   * band. `position: sticky` does not pin an element that has not yet reached its offset,
   * so for the first ~513px of scroll the rail sits at its natural position and its
   * bottom is off screen. The hero moving between the title and the controls is what
   * turned a documented ~271px residual into a 720px one.
   *
   * ## Why this fix and not a clamped height
   *
   * The alternative is to size the rail to the space actually available — `viewport -
   * currentTop - gutter` — recomputed on scroll. It removes the clipping and replaces it
   * with something worse: at scroll 0 that expression yields **55px**, a panel that is a
   * header and nothing else. It also needs a scroll listener reflowing the panel
   * continuously, against a file whose height is a constant expression precisely because
   * measurement has misled here before.
   *
   * Scrolling the rail to its pinned slot is one motion, in direct response to a click
   * that means "I want to edit the layout", and it leaves the panel at the geometry it
   * was designed for. `overflow: hidden` is not considered at all: it would hide the
   * symptom and leave the reader unable to reach the rows underneath it.
   *
   * `block: 'nearest'` with `scroll-margin` on the rail (see the `Collapse` `sx`) does the
   * whole thing: it scrolls the MINIMUM needed and honours the margins, so it lands the
   * rail's top exactly on `pinnedChromeHeight` and does nothing at all when the rail is
   * already pinned. Verified: scrollTop 0 -> 513, panel `207.7 -> 776.2`, 568.5 of 568.5
   * visible.
   *
   * A LAYOUT effect, so the scroll is committed before paint and the un-scrolled frame is
   * never shown. Skipped while a card is expanded, where the rail is zero-height and
   * `in={false}` anyway.
   */
  const railRef = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    if (!customizeOpen || expandedId) return
    railRef.current?.scrollIntoView({ block: 'nearest' })
  }, [customizeOpen, expandedId])

  /**
   * "Reopen the drawer at this dimension" — raised by the applied-filter chips and
   * consumed by the filter drawer inside the toolbar.
   *
   * It lives HERE, on the page, because the two halves are on opposite sides of the
   * sticky boundary: the chips render below the pinned band (they must — see
   * `AnalyticsAppliedFilters` constraint 4) and the drawer lives inside it, so the page
   * is the lowest node they share. The token is what makes the same chip re-openable;
   * `FilterOpenRequest` carries the argument.
   */
  const [filterOpenRequest, setFilterOpenRequest] = useState<FilterOpenRequest | null>(null)
  const requestOpenFilter = useCallback(
    (field: string) =>
      setFilterOpenRequest(prev => ({ field, token: (prev?.token ?? 0) + 1 })),
    [],
  )

  /**
   * The pinned control block's measured height, which the Customize panel needs so the two
   * sticky surfaces do not land on top of each other.
   *
   * Both pin against the same scrollport, so with the panel's original `top: 0` they would
   * BOTH come to rest 24px below the window and the full-width band would be drawn over the
   * panel's first ~143px. Handing the panel this number as its own `top` puts it
   * immediately below the band instead, and the same number is subtracted from its height
   * so its bottom still lands on the page's content box.
   *
   * ⚠️ This number dropped when the title left this block (measured 211 → 143), and it is a
   * measurement precisely so that nothing had to be re-tuned for it. The title is back — see
   * `titleBandHeight` — and this number did NOT change, because the title is a SEPARATE
   * sticky box rather than a row inside this one. What the rail needs is the SUM; see
   * `pinnedChromeHeight`.
   */
  const [stickyHeaderRef, stickyHeaderHeight] = useMeasuredHeight()

  /**
   * The pinned TITLE band's height — the page header plus the 12px it carries below itself.
   *
   * Two jobs, and both are why it is measured rather than a constant:
   *
   *  1. It is the control band's sticky `top`, so the two boxes come to rest flush. A
   *     literal here would drift the moment the header's own geometry moves — and it has
   *     moved twice already (the §29 datum lift, and the `pb: 6` → `pb: 3` override below).
   *  2. It is half of `pinnedChromeHeight`, which the Customize rail pins against.
   *
   * ⚠️ The box it measures is a FLEX COLUMN, and that is load-bearing rather than stylistic.
   * `Header` carries `marginBottom: -24.5px` as the other half of its datum lift (§29 — see
   * `Header`'s own note on why `top` alone would leave a dead band). In a plain block wrapper
   * that negative margin COLLAPSES out to the wrapper's own margin, so the wrapper's
   * `getBoundingClientRect().height` reads 81 while the distance to the next element is 56.5
   * — and the control band would then pin 24.5px too low, opening a gap the hero scrolls
   * through. A flex container does not collapse its children's margins, so the measured
   * height and the visible band are the same number.
   */
  const [titleStickyRef, titleBandHeight] = useMeasuredHeight()

  /**
   * Everything that is pinned, top to bottom: the title band plus the control band.
   *
   * The Customize rail pins BELOW all of it, so it is this sum and not `stickyHeaderHeight`
   * alone that the rail's `top` and its viewport-derived height are expressed against. Before
   * the title was pinned the two were the same number, which is why the rail read
   * `stickyHeaderHeight` directly.
   */
  const pinnedChromeHeight = titleBandHeight + stickyHeaderHeight

  /**
   * ── THE STUCK-STATE HAIRLINE UNDER THE CONTROL BAND ──────────────────────────────
   *
   * True once the control band has come to rest at its sticky offset, i.e. once it is
   * actually OVERLAYING content rather than sitting in flow. Drives a 1px rule along its
   * bottom edge; see the band's `::after` for the value and why it is drawn that way.
   *
   * ## Why the LOWER band and not the title
   *
   * Both boxes are sticky and they come to rest flush, so at any scrolled position they
   * read as one block of chrome. Two hairlines a few pixels apart read as a seam rather
   * than a boundary (`StickyFormHeader`'s `stuckEdge` prop records the same finding from
   * Studio: 17px apart, same weight, `rgb(39,39,40)` against `rgb(39,39,42)`, which no eye
   * separates). The line's job is separating chrome from the content passing under it, so
   * it belongs to whichever band is lowermost — here, always this one.
   *
   * ## Why a geometric test and not `scrollTop > 0`
   *
   * `StickyFormHeader` can use `scrollTop > 0` because it pins at the very top of the
   * scrollport, so any scroll at all makes it stuck. This band pins BELOW the title band
   * with the ROI hero between them (§68 — Analytics deliberately pins its controls and lets
   * its title scroll), so for the first ~170px of scroll it is still in flow, covering
   * nothing. A rule there would be a line drawn across the middle of a page.
   *
   * So it compares the band's own top against where it comes to rest.
   *
   * ⚠️ THAT RESTING POSITION INCLUDES THE SCROLLPORT'S OWN TOP PADDING, and leaving it out
   * is a silent off-by-24 that never fires the line. A sticky box parks at its scroll
   * container's PADDING edge, not its border edge, and `Layout`'s scroller carries `p: 6`.
   * Measured here with the band pinned: scrollport top 0, `padding-top` 24, this box's
   * sticky `top` 56.5 (the title band's height), and the band resting at 81 — which is
   * 0 + 24 + 56.5, not 0 + 56.5. Reading the padding rather than hard-coding 24 keeps this
   * correct if `Layout`'s gutter ever moves. The 1px tolerance absorbs sub-pixel layout.
   *
   * ## ⚠️ Not gated on the page having overflowed at mount
   *
   * The listener attaches unconditionally, per `getScrollParent`'s own note: Analytics is
   * data-driven, so at first render it is a skeleton and nothing overflows yet. A version
   * that required `scrollHeight > clientHeight` would attach nothing, and the hairline
   * would never appear once the data arrived — which reads as a styling bug and is really a
   * missing subscription.
   */
  const [bandStuck, setBandStuck] = useState(false)

  useEffect(() => {
    const el = stickyHeaderRef.current
    const sc = getScrollParent(el)
    if (!el || !sc) return
    const read = () => {
      const scrollportPadTop = parseFloat(getComputedStyle(sc).paddingTop) || 0
      // Compared in PAINTED px, like the two rects: the padding (computed style) and the
      // band height (`useMeasuredHeight`) are layout px, so they are scaled by the
      // scroller's text zoom before being added to its rect.
      const restingTop =
        sc.getBoundingClientRect().top + (scrollportPadTop + titleBandHeight) * zoomOf(sc)
      setBandStuck(el.getBoundingClientRect().top <= restingTop + 1)
    }
    sc.addEventListener('scroll', read, { passive: true })
    window.addEventListener('resize', read)
    // Also read at subscribe time: a remount (route change, HMR) can land with the page
    // already scrolled, and without this the band would sit pinned with no rule until the
    // next scroll event.
    read()
    return () => {
      sc.removeEventListener('scroll', read)
      window.removeEventListener('resize', read)
    }
    // `titleBandHeight` is this box's sticky offset, so a change to it moves the resting
    // position the test compares against — re-subscribe rather than close over a stale one.
  }, [stickyHeaderRef, titleBandHeight])

  /**
   * The ROI hero band's height, including its bottom margin.
   *
   * Handed to the canvas as `collapsingChromeAbovePx`. The canvas seeds the expanded
   * region's top SYNCHRONOUSLY in the click handler — before React re-renders, so while
   * the hero is still on screen — and this is the number it has to subtract for the
   * expanded card to start immediately below the action line rather than below where the
   * hero used to be. See that prop for the whole chain.
   *
   * ⚠️ `useMeasuredHeight` returns the LAST measurement when the node is gone, which is
   * what makes this work at all: the hero unmounts the instant a card expands, so a hook
   * that reported 0 for a detached node would zero the offset in the same commit that
   * needs it.
   */
  const [heroRef, heroHeight] = useMeasuredHeight()

  /**
   * Honoured for the panel's slide (§ the shell's `CONTROLS_TRANSITION` does the same for
   * its hover cross-fade). A width transition is a large, page-reflowing motion — precisely
   * the class the setting is about — so it is skipped entirely rather than shortened.
   */
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)')

  /**
   * Bumped whenever the canvas's width has finished changing, so the grid re-measures
   * against the width it ACTUALLY ended at.
   *
   * The old value was `customizeOpen ? 1 : 0` — a single flip at the START of the change.
   * That was correct while the panel appeared instantly; now that the width is animated,
   * a measurement taken on the click reads the width the canvas is leaving. In a real
   * browser the canvas's own `ResizeObserver` tracks the animation continuously and this
   * is belt-and-braces; in the preview pane, where that observer never fires, it is the
   * only thing that gets the grid to its final width. Hence BOTH edges: the flip below on
   * `customizeOpen` (so the grid starts moving) and the bump on the transition's end.
   */
  const [canvasWidthNonce, setCanvasWidthNonce] = useState(0)
  const bumpCanvasWidth = useCallback(() => setCanvasWidthNonce(n => n + 1), [])

  /**
   * The card being dragged out of the library, or null.
   *
   * Lives here rather than inside either component because it is the one fact BOTH
   * need: the panel writes it on `dragstart`, and the canvas reads it to size the drop
   * placeholder before the pointer has entered the grid.
   */
  const [droppingCardId, setDroppingCardId] = useState<string | null>(null)

  /**
   * VERA-260 — the id of a CANVAS card currently being dragged by its own
   * compose-source handle, or null. `droppingCardId` above is shared by both drag
   * sources (a library row's `dragstart` and this one), so this is the one extra bit
   * that tells `AnalyticsCanvas` "this specific drag started FROM the canvas" — see
   * that component's own prop doc for what it changes (self-target exclusion, and
   * treating a miss as a cancel rather than an add/reposition).
   */
  const [composeSourceCardId, setComposeSourceCardId] = useState<string | null>(null)

  /**
   * Every id the page can render, derived from the MODULE CONSTANTS rather than from
   * the `cards` array below.
   *
   * That indirection is load-bearing: that array closes over `totalsData`, so it
   * changes identity on every fetch. Feeding it to `useAnalyticsView` would change
   * `applyState`'s identity on every fetch too, and `ViewsDropdown` holds it. The id
   * SET is static, so `[]` deps are honest here.
   */
  const availableCardIds = useMemo(() => {
    const metricIds = [
      ...Object.values(ANALYTICS_TILE_IDS),
      ...Object.values(ALL_ROI_CARDS).flat().map(card => card.id),
    ]
    return [
      ...metricIds,
      /*
        VRG-467 — the chart / map variants of those same metrics.

        ⚠️ `ANALYTICS_PANEL_IDS` is NOT spread here any more, and that is a removal rather
        than an omission: since VRG-481 those constants ARE variant ids (`scans_count::chart`
        and friends), so spreading them alongside this line would put every panel in the list
        twice. The seeds still name them, because a seed wants to say "the scans chart"
        without re-deriving the id; this list wants "every id the page can render", which is
        exactly what the derivation below produces.
        The two ids they REPLACE — `scans_by_time`, `scans_by_location` — are deliberately
        absent: they no longer render anything, and a saved view holding one is forward-mapped
        to the variant on read (`migrateAnalyticsCardId`) before it is checked against this
        list.

        Computed from the SAME capability function the cards below are built with, so the
        two cannot disagree about which variants exist. It belongs in this `[]`-dep memo
        for the reason stated above and because capability is a function of TYPES, not of
        fetched data — `analyticsDisplayCapability` reads the shape of the statistics
        response and the dimension unions the chart and map components accept, none of
        which change at runtime. A capability that depended on the DATA (say, "chartable
        only if this range has points") would not belong here and would not belong in a
        library affordance at all: it would make a card appear and disappear as the date
        picker moved.
      */
      ...analyticsDisplayVariantIds(metricIds),
    ]
  }, [])

  const view = useAnalyticsView({ availableCardIds })

  /**
   * ── VRG-467: EVERY METRIC SOURCE, KEYED BY ID ───────────────────────────────────────
   *
   * The SAME (id, title) pairs handed to `buildAnalyticsDisplayVariantCards` below,
   * extracted so `buildComposedDisplayVariantCard` can resolve a COMPOSED card's other
   * metrics — the ones a reader dragged onto an existing chart, which are not enumerable
   * up front the way the single-metric variants are (see that function's own header).
   * `[]` deps for the same reason `availableCardIds` above has them: both source arrays
   * (`OVERVIEW_STAT_BOXES`, `ALL_ROI_CARDS`) are module constants, not derived from
   * fetched data.
   */
  const metricSources = useMemo<DisplayVariantSource[]>(
    () => [
      ...OVERVIEW_STAT_BOXES.map(box => ({ id: box.key, title: box.title })),
      ...Object.values(ALL_ROI_CARDS)
        .flat()
        .map(card => ({ id: card.id, title: card.title })),
    ],
    [],
  )
  const metricSourceById = useMemo(
    () => new Map(metricSources.map(s => [s.id, s])),
    [metricSources],
  )

  /**
   * ── VRG-467: EVERY COMPOSED CARD CURRENTLY ON THE CANVAS ────────────────────────────
   *
   * Built once PER COMPOSED ID present in `view.cardIds`, not enumerated up front — a
   * composed id is created by the drag gesture (`AnalyticsCanvas.onComposeDrop` ->
   * `view.composeCard`), so it cannot be known in advance the way the single-metric
   * `(metric, display)` pairs below are. A stale or unresolvable id degrades to `null`
   * (see `buildComposedDisplayVariantCard`'s header) and is simply omitted here — the
   * canvas already renders a graceful hole for any `cardIds` entry with no matching card.
   */
  const composedCards = useMemo<AnalyticsCardItem[]>(() => {
    const built: AnalyticsCardItem[] = []
    for (const id of view.cardIds) {
      const parsed = parseAnalyticsVariantCardId(id)
      if (!parsed || parsed.metricIds.length < 2) continue
      const card = buildComposedDisplayVariantCard(id, metricSourceById, {
        filters,
        scansMapMode,
        onScansMapModeChange: setScansMapMode,
        topInfluencerDimension,
        onTopInfluencerDimensionChange: setTopInfluencerDimension,
        // Required by the shared context type; a COMPOSED card is chart-only (a map
        // refuses composition — see `analyticsComposeTargetRefusal`), so nothing in this
        // branch renders a grouping toggle. Passed rather than made optional: an optional
        // handler would let a real map card be built with no way to regroup it.
        onCardGroupingChange: view.setCardGrouping,
        onRemoveSeries: view.removeCardSeries,
      })
      if (card) built.push(card)
    }
    return built
  }, [
    view.cardIds,
    metricSourceById,
    filters,
    scansMapMode,
    topInfluencerDimension,
    view.removeCardSeries,
    view.setCardGrouping,
  ])

  /**
   * Every card the canvas can place: the four KPI tiles, every ROI card, then the two
   * panels.
   *
   * ONE list, not a tiles list and a panels list. The grid has no zones — a card's
   * `kind` decides its default size, its surface mode and where its drag handle lands,
   * and nothing else depends on the split. Two arrays would have to be merged for the
   * card library and merged again for the canvas.
   *
   * KPI and ROI cards both render through `AnalyticsBlock` and both declare
   * `surface: 'own'`, because that component draws its own `Card` and is shared with
   * Tags → Inventory and Billing — it must not grow an analytics-only header.
   */
  const cards = useMemo<AnalyticsCardItem[]>(
    () => [
      /*
        THE PANELS COME FIRST, ahead of every tile.

        The library is scanned, not read end to end, and this array IS its order. Built
        tiles-first, the charts and the maps sat beneath ~15 metric tiles — the entries a
        reader is most likely to be hunting for, in the one position that guarantees a
        scroll to reach them. They are also the only entries that are structurally distinct
        (`kind: panel`, full-width, resizable), so leading with them orders the list by
        descending size rather than burying them in a long run of identical small ones.

        Charts before maps matches `ANALYTICS_CARD_CATEGORIES` (chart order 1, map 2),
        which is what the display FILTER above the list is sorted by. Two orders that
        disagree would make the filter read as a reshuffle rather than a narrowing.

        ⚠️ THE TWO STANDALONE PANEL CARDS THAT USED TO SIT HERE ARE GONE (VRG-481).
        `scans_by_time` and `scans_by_location` were the same measurements the variants
        below draw, reached through a Scans/Certificates toggle in their headers instead of
        through the metric you picked — so the library carried three rows for one thing.
        What the toggle switched between is now two cards you can hold at once, and the
        header controls that did NOT depend on the dimension (the legend, the mark toggle,
        the map's all/latest mode) moved onto the variants rather than being dropped. See
        `analyticsDisplayVariants` for both halves, and `migrateAnalyticsCardId` for how a
        saved view holding either id still opens.
      */
      /*
        VRG-467 — the DISPLAY VARIANTS. They ARE the panels now.

        They are panels, so they lead the array — the library is scanned, and its order is
        this array. Only pairs `analyticsDisplayCapability` reports as capable produce an
        entry, so this spread is FOUR cards today and would be zero if the statistics
        endpoint stopped bucketing anything. Nothing here decides what is possible; it only
        renders what is.

        ⚠️ Since VRG-481 they are no longer LISTED as library rows — the Layout panel filters
        variant ids out and offers each one through its metric's `Show as` chips instead. They
        still have to be in THIS array: it is what the canvas renders from, and a view holding
        a variant id with no card here would render a hole.
      */
      ...buildAnalyticsDisplayVariantCards(
        // `metricSources` — the same (id, title) pairs `composedCards` above resolves a
        // composed card's OTHER metrics from. One array, not two independently maintained
        // literals: an id present in one and not the other is exactly the drift that
        // produces a hole in a view that references it.
        metricSources,
        // The map's all/latest mode, lifted to this page because the toggle renders into
        // the card shell's HEADER while the map it drives renders in the shell's BODY.
        {
          filters,
          scansMapMode,
          onScansMapModeChange: setScansMapMode,
          // The leaderboard's grouping axis, lifted for the same header/body reason.
          topInfluencerDimension,
          onTopInfluencerDimensionChange: setTopInfluencerDimension,
          // VERA-260 — the map's `Group by:` toggle. Not page STATE like the two above:
          // grouping lives in the card's id, so the handler is a RENAME. See
          // `useAnalyticsView.setCardGrouping`.
          onCardGroupingChange: view.setCardGrouping,
        },
      ),
      /*
        VRG-467 — every COMPOSED card currently on the canvas. Alongside the enumerated
        single-metric variants above for the same reason `scans_by_time` used to sit
        beside them: this array is what the canvas renders FROM, and a `cardIds` entry
        with no matching card here is a hole.
      */
      ...composedCards,
      ...OVERVIEW_STAT_BOXES.map(box => ({
        // A KPI's id IS the totals field it displays, which is what keeps this list and
        // `ANALYTICS_TILE_IDS` in agreement by construction rather than by convention.
        id: box.key,
        kind: 'tile' as const,
        title: box.title,
        category: 'metric' as const,
        summary: KPI_SUMMARIES[box.key],
        surface: 'own' as const,
        render: () => (
          <OverviewStat
            box={box}
            totals={totalsData?.data ?? {}}
            // ABSENT, not zeroed, when the server did not compare — a missing
            // comparison and a zero one are different facts.
            previous={totalsData?.data?.previous}
            interval={describePreviousRange(totalsData?.data?.previous_range)}
            // First load only — see `totalsFirstLoad`. A filter change keeps the
            // previous numbers on screen and swaps them in place.
            isLoading={totalsFirstLoad}
          />
        ),
      })),
      ...Array.from(roiCardsById.values()).map(card => ({
        id: card.id,
        kind: 'tile' as const,
        title: card.title,
        category: 'metric' as const,
        // The card's OWN description, not a second sentence written here — it is
        // already the tooltip's text, and two copies of a definition drift (§44).
        summary: card.description,
        surface: 'own' as const,
        render: () =>
          // First load only — see `roiFirstLoad`.
          roiFirstLoad ? (
            <Skeleton variant="rounded" height={CARD_MIN_HEIGHT} sx={{ borderRadius: 3 }} />
          ) : (
            // See Overview.tsx: the grid cell owns the height, so the floor must not
            // also be a licence to grow past it.
            <Box sx={{ display: 'flex', height: '100%', minHeight: CARD_MIN_HEIGHT, maxHeight: '100%', '& > *': { flex: 1, minHeight: 0 } }}>
              <AnalyticsBlock
                title={card.title}
                term={card.term}
                /*
                  Three states in the figure slot, and the third is the whole of
                  VRG-459's in-card half:

                    a figure                 the metric computed.
                    `N inputs needed`        the server withheld it with reason
                                             `needs_input`, so the brand can unblock it.
                    an em-dash               everything else. A blocked metric has no
                                             figure and rendering zero would assert one;
                                             a `not_computed` one has every input set and
                                             still cannot be measured.

                  ⚠️ The button is NOT offered on the em-dash cases. Sending a brand to a
                  form that cannot change the number is worse than the em-dash — it is
                  the specific experience that makes people stop trusting a dashboard.
                  `card.needsInputs` is populated ONLY from `withheld[].reason ===
                  'needs_input'` (see `useRoiCards`), never from "has no value".
                */
                stat={
                  card.needsInputs && card.needsInputs.length > 0 && card.metricId ? (
                    <MetricInputsNeededButton
                      metricId={card.metricId}
                      metricTitle={card.title}
                      missing={card.needsInputs}
                      roi={roiData?.roi ?? null}
                      // The change IS the feedback (VRG-459), so the card the popover
                      // is anchored to updates in place rather than the user being
                      // navigated anywhere.
                      //
                      // ⚠️ THIS DOES NOT REACH THE HERO. `RoiHero` owns a SECOND redux
                      // slot with its own period and its own request
                      // (`Actions.GET_ANALYTICS_ROI_HERO`), and nothing outside it knows
                      // that period, so `Partial · N of M sources` and the multiple
                      // still need a reload. Closing that needs a refetch trigger inside
                      // `RoiHero` itself; it is reported rather than faked from here.
                      onSaved={() => refetchRoi()}
                    />
                  ) : (
                    // The shared constant, not a literal: `AnalyticsBlock` tests the figure
                    // against it to decide whether a caveat stays on the card face.
                    card.value ?? EM_DASH_PLACEHOLDER
                  )
                }
                unit={card.unit}
                // The blocked / not-computed reason shares the caveat slot with `note`.
                // `AnalyticsBlock` decides where it lands — the `(i)` when the card has a
                // figure, the card face in the secondary tone when it does not, which is
                // always true of a blocked reason. See that component's `note` prop for
                // why the amber inline line was retired.
                note={card.note ?? card.blockedReason}
                tooltip={card.description}
              />
            </Box>
          ),
      })),
    ],
    // `totalsFirstLoad` / `roiFirstLoad` rather than the raw `isLoading` flags: those are
    // what the render closures above read now, and listing a value nothing reads would
    // rebuild every card on each refetch for no reason.
    // `roiData` joins the list for VRG-459's popover, which reads the SUMMARY half of
    // the response (`roiData.roi`) for its consequence echo. `roiCardsById` is derived
    // from the same response but carries only what a card renders, so it is not a
    // stand-in for it here.
    // `metricSources` / `composedCards` — VRG-467's enumerated-source list and its
    // COMPOSED cards, both already memoised above with their own tighter dependencies.
    [
      totalsData,
      totalsFirstLoad,
      roiCardsById,
      roiData,
      roiFirstLoad,
      refetchRoi,
      filters,
      scansMapMode,
      topInfluencerDimension,
      metricSources,
      composedCards,
      // VERA-260 — the grouping toggle's handler, read by every map card's `action`.
      view.setCardGrouping,
    ],
  )

  /**
   * ── THE DOWNLOAD (VERA-260) — one CSV per card on the canvas, as a zip ──────────────
   *
   * The page owns this rather than the toolbar because the export's subject is the VIEW,
   * and the view's three inputs meet only here: `view.cardIds` (what is on the canvas),
   * `filters` (what narrows it) and the two lifted card controls (`scansMapMode`,
   * `topInfluencerDimension`) that change what a map and a leaderboard actually return.
   * Everything past that point is in `analyticsExport` — see its header for the file
   * shape and for why the provenance block is not optional.
   *
   * ⚠️ ASYNC, WHERE THE OLD HANDLER WAS SYNCHRONOUS. Three of the five card shapes fetch
   * their own data, so the export issues real requests (concurrently — see
   * `buildAnalyticsExport`). That is what `exporting` is for: without it the button is a
   * control that appears to do nothing for a second or two, and the reader's response to
   * a control that appears to do nothing is to press it again, which starts a second
   * export.
   */
  const [exporting, setExporting] = useState(false)
  const { open: notify } = useSnackbar()

  const handleDownloadCSV = useCallback(async () => {
    if (exporting) return
    setExporting(true)
    try {
      const result = await downloadAnalyticsExport({
        cardIds: view.cardIds,
        cards,
        filters,
        companyId: DEFAULT_COMPANY_ID,
        // The company's own display name, so a file that has left this app still says
        // whose figures it holds. `Company` is the same record the page already fetched
        // for the prior-period gate, so this costs no extra request.
        companyName: company?.name ?? 'Vera',
        /*
          THE COMPANY'S REPORTING CURRENCY, read off the ROI response rather than off the
          company record — `IRoiMetrics.reporting_currency` is the currency the figures in
          THIS response were computed under, which is the fact the file has to state (see
          `analyticsMoney`: it decides which rows are IN the money aggregates, not merely
          how they are drawn). Falling back to the company record, then to the shared
          default, so a file is never stamped with a blank currency.
        */
        reportingCurrency:
          roiData?.data?.reporting_currency ??
          company?.reporting_currency ??
          DEFAULT_REPORTING_CURRENCY,
        totals: totalsData?.data,
        // Only the three fields the export reads, so it never has to touch `value` — a
        // ReactNode on the assumption-backed cards. See `ROICardDef.rawValue`.
        roiCards: new Map(
          Array.from(roiCardsById.values()).map(card => [
            card.id,
            {
              id: card.id,
              title: card.title,
              /*
                `rawValue` first, then `value` ONLY when it is a string.

                The fallback is what keeps this from needing a `rawValue` on all ~30
                branches: most of them already produce an unformatted `String(n)` /
                `n.toFixed(1)`. An ELEMENT value falls through to `null`, which exports an
                empty cell — correct, because the alternative is `[object Object]` in a
                column a reader would otherwise sum.
              */
              rawValue:
                card.rawValue ?? (typeof card.value === 'string' ? card.value : null),
              unit: card.unit,
              note: card.note ?? card.blockedReason,
            },
          ]),
        ),
        scansMapMode,
        topInfluencerDimension,
      })

      /*
        A TOAST ON SUCCESS, because the result of this action is invisible.

        The toast policy is that a VISIBLE action needs no toast and an invisible one does.
        A file landing in the browser's download shelf is exactly the invisible case — on
        a browser configured to save silently there is nothing on screen at all — and the
        count is the part worth saying, since it is what a reader would otherwise have to
        open the archive to learn.
      */
      const skipped = result.skipped.length
      notify({
        type: 'success',
        title: `Exported ${result.fileCount} ${result.fileCount === 1 ? 'file' : 'files'}`,
        text: skipped
          ? `${skipped} ${skipped === 1 ? 'card' : 'cards'} had no data to export. See 00-not-exported.csv in the archive.`
          : undefined,
      })
    } catch (error) {
      // The whole export failed — a zip that cannot be built, or every request erroring.
      // Reported rather than swallowed: a Download button that does nothing at all is
      // indistinguishable from one that is broken, which is what this used to look like.
      notify({
        type: 'error',
        title: 'Could not build the export',
        text: error instanceof Error ? error.message : undefined,
      })
    } finally {
      setExporting(false)
    }
  }, [
    exporting,
    view.cardIds,
    cards,
    filters,
    company,
    roiData,
    totalsData,
    roiCardsById,
    scansMapMode,
    topInfluencerDimension,
    notify,
  ])

  return (
    /**
     * NO PAGE PADDING. `Layout`'s scroll container already carries `p: 6` (24px) for every
     * page in the app; this page used to add a second `p={6}` on top of it, so Analytics
     * rendered at DOUBLE the gutter of every other surface. Measured at 1280×800 before the
     * fix: `/certificates` 24px left and right with a 16px search→content gap, `/analytics`
     * 48px and 40px — exactly 24 more on both axes.
     *
     * ⚠️ Four constants were tuned against that 48 and moved with it. Do not re-add padding
     * here without walking all of them: the sticky band's `left`/`right` bleed below,
     * `STICKY_BLEED_PX`, `PANEL_VIEWPORT_RESERVE_PX` (`AnalyticsCustomize`) and
     * `PAGE_GUTTER_PX` (`AnalyticsCanvas`).
     *
     * 🚨 "The bleed fails loudly" USED TO BE WRITTEN HERE, AND IT WAS WRONG BY HALF. It is
     * true of the HORIZONTAL bleed: that one is a negative inset, so an over-wide value
     * pushes the band past the scrollport, and `scrollWidth !== clientWidth` reports it in
     * one line (`Layout`'s `overflowX: hidden` hides the symptom but not the measurement).
     * It is FALSE of the VERTICAL bleed, which was left at 48 on exactly that reasoning and
     * spent weeks painting the page background over the bottom 24px of the ROI hero. It
     * fails SILENTLY: an opaque box covering a card changes no rect, no scroll dimension and
     * no overflow flag, so every geometric check keeps passing. See `STICKY_BLEED_PX` for
     * the measurement that does catch it (`elementFromPoint`, not `getBoundingClientRect`).
     */
    <Box>
      {/* Analytics was the only main page with no title. Via the shared `Header`, so it
          inherits the sidebar-logo datum alignment — that offset is computed inside the
          component and must never be nudged with a per-page `pt` (§29).

          IN ITS OWN STICKY BOX, pinned at `top: 0`, with the control band pinned below it at
          the height this box measures. See the sticky-block note at the top of the file for
          why there are two boxes rather than one, and for the band's measured cost. */}
      {/*
        No `description`, deliberately. The one that was here did two jobs and both were
        weak: "Scan and certificate activity for the selected period" restated the title
        plus the date range immediately below it, and "Drag any card to move it, and
        resize the panels" is instructional chrome — true on the first visit and noise on
        every one after, for an affordance the drag grip's own tooltip already names.

        §40 governs where a page description GOES, not that a page must have one. Add one
        here only if it states something the surface cannot show for itself, e.g. a
        constraint on what the numbers cover.
      */}
      {/**
        * `pb: 3` (12px), down from the shared `Header`'s own `pb: 6` (24px).
        *
        * PARITY WITH ROLES & PERMISSIONS, the page the user named as the reference. There
        * the tab track is rendered INSIDE `Header` (`stackControls`), so the title→track
        * gap is that component's `spacing={3}` — 12px, plus the 8.9px of slack the title's
        * 31.2px line box leaves inside its 49px row. Here the next thing is a SIBLING of
        * `Header`, so the same 12px has to come from the header's bottom padding instead.
        *
        * AT THE CALL SITE, not in `Header`. `pb: 6` is the title→content gutter every one
        * of the ~90 pages using this component depends on; changing it there to fix one
        * page would move all of them, and Roles would not even be among them (it gets its
        * 12px from `spacing`, not from `pb`). This page is the outlier precisely because
        * its view controls are not in the header, so the override belongs here.
        *
        * §29 forbids nudging the header's TOP with a per-page `pt` — that is the sidebar
        * logo datum, and it is untouched: the title's position does not move, only what
        * follows it.
        */}
      <Box
        ref={titleStickyRef}
        sx={{
          position: 'sticky',
          top: `${STICKY_TOP_PX}px`,
          zIndex: STICKY_Z_INDEX,
          // Opaque, for the same reason the control band is: the hero scrolls up UNDER this
          // box. `background.default`, not `background.paper` — this is the page behind the
          // cards, not a card.
          backgroundColor: 'background.default',
          /**
           * FLEX COLUMN, and it is not a style choice — see `titleBandHeight`. `Header`'s
           * datum lift (§29) is a `position: relative; top: -24.5px` PLUS a matching
           * `marginBottom: -24.5px` that reclaims the vacated space. In a block wrapper that
           * negative margin collapses out to the wrapper's own margin, so the wrapper
           * MEASURES 24.5px taller than the band you can see — and the control band, whose
           * sticky `top` is that measurement, would pin 24.5px too low and leave a gap the
           * hero scrolls through. A flex container does not collapse its children's margins.
           *
           * The lift itself is untouched: the title still centres on the sidebar mark's 44px
           * datum, and pinning does not move it because the box comes to rest at exactly the
           * y it already occupies at scroll 0 (`top: 0` → 24px, `Layout`'s padding).
           */
          display: 'flex',
          flexDirection: 'column',
          /**
           * The 24px strip above the pinned band — `Layout`'s scroll padding, which is inside
           * the scrollport, so content scrolls THROUGH it unless something paints it.
           *
           * 🚨 THIS MOVED HERE FROM THE CONTROL BAND, which no longer draws one. The strip
           * above the control band is now the BOTTOM of this box, already opaque; a second
           * bleed there would have painted over the last 24px of this band and clipped the
           * title's descenders. See `STICKY_BLEED_PX` for the measurement.
           *
           * `Header`'s -24.5px lift paints ABOVE this box's own top edge, into this strip.
           * That is safe and not accidental: `::before` and the header are both positioned
           * with `z-index: auto` in the same stacking context, so they paint in tree order
           * and the header — a later sibling — lands on top of the band rather than under it.
           */
          '&::before': {
            content: '""',
            position: 'absolute',
            /**
             * ONE gutter, `Layout`'s 24px — this page no longer adds its own (see the root
             * `Box`). The band has to reach across it, or content scrolling up past the
             * header shows in the margin beside it.
             *
             * ⚠️ This is a NEGATIVE inset, so it must EQUAL the gutter and never exceed it.
             * It was -48 while the page carried a second 24px of its own; left at -48 with
             * one gutter the band overhangs the scrollport by 24px on each side, which is
             * horizontal overflow that `Layout`'s `overflowX: hidden` silently clips rather
             * than reports. Verified after the change: `scrollWidth === clientWidth` with the
             * Customize panel both open and closed.
             */
            left: -24,
            right: -24,
            top: -STICKY_BLEED_PX,
            height: STICKY_BLEED_PX,
            backgroundColor: 'inherit',
          },
        }}
      >
        <Header title="Analytics" sx={{ pb: 3 }} />
      </Box>

      {/**
        * ── THE ROI HERO, ABOVE THE CONTROLS ──────────────────────────────────────────
        *
        * It sits between the title and the Scans/Certificates + date row, not below them,
        * because THE CONTROLS BELOW DO NOT SCOPE IT. The toggle swaps every other card on
        * the page between scans and certificates and does nothing at all to the hero, and
        * the date picker no longer reaches it either — the hero owns its own period (see
        * `RoiHero`, and `RoiHeroPeriod` for why the billing period is the default). A
        * control rendered above a card it does not affect is a false statement about what
        * that card shows, and it is the kind of false statement a reader never checks.
        *
        * It does NOT stick, and it is now the one thing between the two boxes that DO. It
        * scrolls up UNDER the title band, which is what lets the title pin without the hero
        * having to move or the two bands having to be adjacent in the DOM. See the sticky
        * block's own note for the arithmetic; the short version is that a ~170px headline
        * added to a 183px pinned chrome would hold ~354px — well over a third of the
        * viewport, for a card that is read once on arrival.
        *
        * HIDDEN while a card is expanded, and that is a geometry requirement rather than
        * tidiness: an expanded card fills the region from immediately below the action
        * line, so anything between the two has to go. `heroHeight` is what tells the canvas
        * how much just left — see `collapsingChromeAbovePx`.
        *
        * The wrapper carries `mb: 6` so the measured height INCLUDES the gap, which is what
        * the canvas actually needs to subtract.
        */}
      {!expandedId && (
        <Box ref={heroRef} sx={{ mb: 6 }}>
          {/**
           * VRG-512 — the sample-data caveat, INSIDE `heroRef` deliberately.
           *
           * Every analytics read path is `INTRO_INCLUSIVE`: the charts and tiles below
           * deliberately COUNT seeded `intro` rows, because filtering them would put
           * the empty state back in front of the user the intro set exists to serve.
           * This banner is what makes that honest, so it belongs with the figures it
           * qualifies rather than in the chrome.
           *
           * Placed inside this wrapper rather than above it for two geometry reasons,
           * both of which the wrapper already solves: its height is then included in
           * the measured `heroHeight` the canvas subtracts (see
           * `collapsingChromeAbovePx`), and it disappears with the hero when a card is
           * expanded — which the note above requires of anything in this region.
           * Putting it outside would need a second measured constant and a second
           * `!expandedId` guard, for no benefit.
           *
           * Renders nothing at all when the tenant holds no intro data, so this costs
           * a hidden component and no layout on every other company.
           */}
          <IntroDataBanner />
          <RoiHero />
        </Box>
      )}

      {/* THE PINNED CONTROL BLOCK — the action line, with the date range inside it. See the
          constants at the top of this file for what sticks and why. */}
      <Box
        ref={stickyHeaderRef}
        sx={{
          position: 'sticky',
          /**
           * BELOW THE TITLE BAND, not at the window edge — this is the second of the two
           * pinned boxes. The offset is the title band's MEASURED height, so the two come to
           * rest flush at any font size or viewport without a constant to keep in sync.
           *
           * ⚠️ NOT `STICKY_TOP_PX`. That is the TITLE box's offset now. Setting this to 0 as
           * well puts both boxes at the same y and this one — later in the DOM at the same
           * `z-index` — paints over the title.
           */
          top: `${titleBandHeight}px`,
          zIndex: STICKY_Z_INDEX,
          // Opaque, or the dashboard scrolls through the header. `background.default` and
          // not `background.paper`: this band is the PAGE behind the cards, not a card.
          backgroundColor: 'background.default',
          // ZERO — `ActionLine`'s own `pb: 16px` is inside this block and already supplies
          // the band's clearance below the controls. See the constant for the measurement.
          pb: `${STICKY_PAD_BOTTOM_PX}px`,
          // NO `::before` bleed here any more — it moved onto the title box, which is the
          // topmost pinned element now. The 24px above THIS box is the bottom of the title
          // band, already opaque; painting it again would clip the title. See
          // `STICKY_BLEED_PX`.
          /**
           * ── THE STUCK EDGE, AND IT IS A HAIRLINE ONLY ────────────────────────────
           *
           * `primary[100]` — settled precedent, not a fresh judgement. Three separate
           * things converge on it: it is what every other sticky header in the app draws
           * (`StickyFormHeader`), it is the same token the cards and the data table outline
           * with, and it is mode-aware. `glassEdge` is not a candidate here — it is
           * `common.black` at 0.06, a light-mode-only value that is invisible on a
           * near-black page.
           *
           * ⚠️ DO NOT RE-TRY A FADE. `StickyFormHeader`'s docblock records the measurement
           * that killed it: the page is `rgb(9,9,11)` and a card is `rgb(15,15,18)`, so a
           * gradient of page-background over a card dissolves near-black into near-black at
           * about 2.7%. Height is not the free variable it looks like — a taller gradient
           * spreads the same delta over more pixels and gets fainter per pixel, not more
           * visible. `primary[100]` is `rgb(39,39,42)` against that same background: about
           * 12%, roughly four times the contrast, on one crisp pixel.
           *
           * ## Why `::after` rather than the `borderBottom` StickyFormHeader uses
           *
           * That component toggles a border between `transparent` and the colour so the
           * block's height never changes WITH SCROLL STATE. Same requirement here, but this
           * band has an additional one: `stickyHeaderHeight` is measured and feeds
           * `pinnedChromeHeight`, which the Customize rail pins against. A transparent
           * border would satisfy the scroll-state half while permanently adding 1px to that
           * measurement, moving the rail. An absolutely positioned 1px strip is outside
           * flow, so the band's height is identical to before this line existed — verified
           * by measuring the band with the rule on and off.
           *
           * Stuck-only: at rest this band covers nothing, and a permanent rule under the
           * controls is chrome the resting state does not need — it would just be a line
           * across the page.
           */
          '&::after': {
            content: '""',
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: '1px',
            backgroundColor: bandStuck ? 'primary.100' : 'transparent',
            transition: 'background-color 0.15s ease',
            pointerEvents: 'none',
          },
        }}
      >
      <AnalyticsFiltersHeader
        onDownload={handleDownloadCSV}
        downloadBusy={exporting}
        filterOpenRequest={filterOpenRequest}
        // Views lands in `ActionLine`'s `beforeFiltersComponents` slot, immediately LEFT of
        // Filter — parity with every other table's saved-views dropdown (`Table.tsx` renders
        // its own `ViewsDropdown` in the same slot).
        beforeFilters={
          <AnalyticsViews
            currentState={view.currentState}
            applyState={view.applyState}
            onReset={view.resetToDefault}
          />
        }
        // "Layout" (the dashboard's card-layout toggle) lands RIGHT of Filter instead —
        // parity with every other table's Layout control, which sits there via the built-in
        // `showLayoutOptions` (`ActionLine` order: rightComponents -> beforeFiltersComponents
        // -> Filters -> Sort -> LayoutOptions). It used to ride along with Views on the LEFT,
        // which made this page's Layout control the one outlier not sitting where "Layout"
        // sits everywhere else.
        afterFilters={
          <AnalyticsCustomizeButton
            open={customizeOpen}
            onToggle={() => setCustomizeOpen(o => !o)}
            // The library edits the canvas, which is not on screen while a card is
            // expanded.
            disabled={!!expandedId}
          />
        }
      />
      </Box>

      {/**
        * ── THE APPLIED-FILTER CHIPS ──────────────────────────────────────────────────
        *
        * OUTSIDE the sticky block, deliberately, and that placement is the constraint
        * rather than a layout preference. This row's height is a function of how many
        * dimensions are applied and how they wrap, and a variable-height region inside a
        * PINNED one is how a band creeps — the block is already 143px (225 with the
        * shell header) and every wrapped chip row would be another ~36px permanently
        * taken from the scroll region. The `Filter` badge stays pinned and keeps
        * carrying the count; the detail scrolls with the content it describes.
        *
        * It renders NOTHING when no filter is applied — no wrapper, no margin — so this
        * costs zero height in the common case. See the component for why that is
        * load-bearing and not an optimisation.
        */}
      <AnalyticsAppliedFilters onOpenFilter={requestOpenFilter} />

      {/* The library is a FLEX SIBLING of the canvas, not an overlay: opening it makes
          the dashboard narrower rather than covering it, which is the only arrangement
          in which you can drag a card from one to the other. Same reasoning, and the
          same chrome, as the Ask Vera panel.

          `useFlexGap` is NOT cosmetic. Without it MUI implements `spacing` by writing
          `margin: 0` onto every child after the first — a shorthand, at higher
          specificity than the child's own class — which silently erases the negative
          margin the sidebar uses to reach the window edge. Measured: the panel's
          computed `margin-right` read `0px` with `mr: -12` set on it. */}
      {/**
        * `spacing={0}`, and the 24px gutter moved ONTO the panel (`ml: 6` below).
        *
        * A flex `gap` is not part of the panel's animated width, so with the panel now
        * permanently mounted (it has to be — a node destroyed on `open === false` can never
        * play an exit) a `spacing={6}` here would hold 24px open across the canvas's right
        * edge whether the panel was showing or not. Folding the gutter into the collapsing
        * region makes the whole 364px appear and disappear as one motion.
        *
        * `alignItems="stretch"` is now inert and kept only as a floor: BOTH children opt
        * out with `alignSelf: 'flex-start'`, so the row's height is the canvas's either
        * way. What the sticky rail actually needs is that the ROW — its containing block —
        * is tall, which the canvas supplies. The rail must NOT stretch, or it would be as
        * tall as the canvas and have no offset left to travel against.
        */}
      <Stack
        useFlexGap
        direction="row"
        spacing={0}
        alignItems="stretch"
        sx={{ mt: `${CANVAS_TOP_GAP_PX}px` }}
      >
        <Box sx={{ flex: 1, minWidth: 0, alignSelf: 'flex-start' }}>
          {/* The hero no longer renders inside the canvas — it is above the sticky
              control block now, since neither the toggle nor the date picker scopes it.
              The one thing the old `hero=` slot bought was the expand geometry: as a
              canvas child it could be hidden internally, so the root's top was always the
              region's top. That is now explicit — the page hides it and hands over its
              height. */}
          <AnalyticsCanvas
            collapsingChromeAbovePx={heroHeight}
            cards={cards}
            cardIds={view.cardIds}
            layouts={view.layouts}
            onLayoutsChange={view.setBreakpointLayout}
            onCardDropped={(id, item, breakpoint) => {
              view.addCardAt(id, item, breakpoint)
              setDroppingCardId(null)
            }}
            // VRG-467 — the drop landed ON an existing chart/map instead of empty canvas
            // space: join it as another series rather than placing a new card.
            onComposeDrop={(targetCardId, metricId) => {
              view.composeCard(targetCardId, metricId)
              // VERA-260 — this drag started from an existing CANVAS card (not the
              // library): a successful merge means that source card's own standalone
              // presence must go too, or the metric renders twice — once as the card
              // that just merged, once as the series it merged INTO. Guarded on
              // `!== targetCardId` defensively; `isComposeDropTarget`'s self-exclusion
              // gate already makes that case unreachable, but a no-op guard here costs
              // nothing and this is the one place a future change to that gate would
              // otherwise silently delete the target it just composed into.
              if (composeSourceCardId && composeSourceCardId !== targetCardId) {
                view.removeCard(composeSourceCardId)
              }
              setDroppingCardId(null)
              setComposeSourceCardId(null)
            }}
            droppingCardId={droppingCardId}
            composeSourceCardId={composeSourceCardId}
            onComposeDragStart={id => {
              setDroppingCardId(id)
              setComposeSourceCardId(id)
            }}
            onComposeDragEnd={() => {
              setDroppingCardId(null)
              setComposeSourceCardId(null)
            }}
            expandedId={expandedId}
            onExpandedChange={setExpandedId}
            // The per-card `X`. Same handler as the Layout panel's remove row, so the
            // two paths cannot diverge — including the geometry the hook remembers so a
            // re-add lands the card back where it was.
            onRemoveCard={view.removeCard}
            // Opening the panel changes the canvas's width with no window `resize`
            // behind it, so the grid has to be told to re-measure — now on BOTH edges of
            // the slide, since the width no longer changes instantly. See the nonce's own
            // note above.
            widthNonce={canvasWidthNonce}
          />
        </Box>

        {/**
          * THE PANEL SLIDES, and the mechanism is a WIDTH animation rather than a transform.
          *
          * The panel is a flex SIBLING of the canvas — opening it makes the dashboard
          * narrower instead of covering it, which is the whole reason a card can be dragged
          * from one to the other. A `Slide` (translateX) would therefore be the wrong motion:
          * the panel would glide in over the top while the canvas snapped to its new width in
          * a single frame underneath, i.e. two motions telling different stories. Animating
          * the width makes the canvas's own `flex: 1` reflow in step, so there is one motion —
          * the canvas gives up the space at exactly the rate the panel takes it.
          *
          * `Collapse orientation="horizontal"` rather than a hand-rolled transition (§28/§47),
          * and there is a second reason beyond reuse: `Collapse` drives its state machine on a
          * `setTimeout`, not on the compositor. Its `entered` state sets `width: auto` and
          * `overflow: visible` when the timer expires, so the FINAL layout is correct even
          * where the animation clock is frozen — which is exactly the Claude preview pane
          * (measured this session: `document.timeline.currentTime` delta 0 over 500ms, rAF 0
          * times, and a raw `transition: width 400ms` element still reading `0px` 700ms in).
          * A hand-rolled CSS transition would leave the panel permanently at width 0 there.
          *
          * `overflow: hidden` on the Collapse root DOES suspend the panel's stickiness while
          * the transition runs, because it makes the root the nearest scroll container. That
          * is the 260ms in which nobody is scrolling, and `entered` restores `overflow:
          * visible`, so the settled state pins normally. Verified rather than assumed.
          */}
        <Collapse
          ref={railRef}
          /**
           * ⚠️ `&& !expandedId` — THE PANEL CLOSES WHILE A CARD OWNS THE REGION.
           *
           * The library edits the CANVAS, and the canvas is not on screen while a card is
           * maximized; `AnalyticsCustomizeButton` already reflects that by disabling itself
           * (`disabled={!!expandedId}` above). But disabling the opener does nothing for a
           * panel that was ALREADY open when the card expanded — it stayed docked beside a
           * maximized card, offering drag sources with nowhere to drop.
           *
           * Closing it is also half of the clip fix below: the rail's height only stops
           * mattering once it is both zero-height AND not showing.
           */
          in={customizeOpen && !expandedId}
          orientation="horizontal"
          timeout={reduceMotion ? 0 : CUSTOMIZE_SLIDE_MS}
          // The grid's final width. In a real browser the canvas's own ResizeObserver has
          // already tracked the whole slide and this changes nothing; in the preview pane
          // it is what gets the grid off the width it started at. Fires on BOTH directions.
          onEntered={bumpCanvasWidth}
          onExited={bumpCanvasWidth}
          // ...and on the leading edge too, so the reflow begins with the motion rather
          // than only landing at the end of it.
          onEnter={bumpCanvasWidth}
          onExit={bumpCanvasWidth}
          sx={{
            flexShrink: 0,
            /**
             * ── THE RAIL IS THE STICKY ELEMENT, AND ITS HEIGHT IS FIXED UP FRONT ────────
             *
             * These three properties used to live on the panel INSIDE this `Collapse`, and
             * that produced the "slides in, then jumps up" the user reported. MUI's
             * horizontal `Collapse` sets `wrapper.style.position = 'absolute'` in its
             * `enter` phase — it must, to measure the child's natural width before it can
             * animate to it — and restores it in `entering`. A `position: sticky`
             * descendant therefore resolves against a DIFFERENT containing block for the
             * first frames and snaps back afterwards. Measured on the broken version: the
             * panel sat at top 483 for the whole slide and landed at 271.5, a 211.5px
             * lurch — exactly `stickyHeaderHeight`. Two motions where the design has one.
             *
             * Hoisting `sticky` / `top` / `height` onto the animating element itself fixes
             * it structurally rather than by timing: the wrapper's absolute phase now
             * happens inside a box whose geometry is already settled, so it cannot move
             * anything visible. WIDTH is then the only property in flight, which is the
             * checkable form of "one motion" — height must read CONSTANT at every point of
             * the transition while width interpolates.
             *
             * The height is a `calc` off the VIEWPORT (`vhMinus`), never off this element's
             * own animating width, so it is correct from the first frame. `alignSelf:
             * flex-start` keeps the rail at its natural height while the row stays tall
             * enough (it is as tall as the canvas) to give the sticky offset somewhere to
             * travel.
             *
             * `overflow: hidden` on this root during the transition does NOT break its own
             * stickiness — an overflow container is a scrollport for its DESCENDANTS, and
             * this element's offset still resolves against the page scroller.
             */
            alignSelf: 'flex-start',
            position: 'sticky',
            // `pinnedChromeHeight`, NOT `stickyHeaderHeight`: the pinned chrome is TWO boxes
            // now (title band + control band) and the rail has to clear both. They were the
            // same number while only the controls pinned.
            top: `${pinnedChromeHeight}px`,
            /**
             * ── THE TWO MARGINS `scrollIntoView` READS ─────────────────────────────────
             *
             * They are what make `block: 'nearest'` land the rail on its pinned slot
             * rather than hard against the scrollport's edges — see the `railRef` effect
             * for the measurements and for why scrolling is the fix rather than a clamped
             * height.
             *
             * `scrollMarginTop` is the SAME expression as `top`, deliberately: the offset
             * the rail comes to rest at IS the offset a scroll should bring it to, so a
             * literal here would drift the moment the pinned chrome's height moved — and
             * it has moved twice already.
             *
             * `scrollMarginBottom` is the page gutter, so the scroll stops with the
             * panel's bottom edge where a card's bottom edge would be rather than flush
             * against the window. `PANEL_VIEWPORT_RESERVE_PX / 2` rather than a second
             * `24`: that constant is the top inset PLUS the bottom gutter, both of which
             * are `Layout`'s one padding value, so taking half of it keeps this tied to
             * the same source instead of restating a number that would then have to be
             * kept in step.
             */
            scrollMarginTop: `${pinnedChromeHeight}px`,
            scrollMarginBottom: `${PANEL_VIEWPORT_RESERVE_PX / 2}px`,
            /**
             * ── 🚨 ZERO WHILE A CARD IS MAXIMIZED. THIS IS THE "EXPAND CLIPS THE TOP" BUG ──
             *
             * The rail is a FLEX SIBLING of the canvas, so the row is as tall as whichever of
             * the two is taller. Normally that is the canvas — the dashboard is ~2000px — and
             * this viewport-derived height is invisible. Maximize a card and the canvas
             * collapses to exactly one screenful, at which point the rail becomes the tallest
             * thing in the row and silently sets the page's height.
             *
             * It does not merely tie; it OVERSHOOTS, by a fixed amount, permanently:
             *
             *   rail            100vh − (PANEL_VIEWPORT_RESERVE_PX 48 + stickyHeaderHeight)
             *   expanded card   100vh − (regionTop + PAGE_GUTTER_PX 24)
             *
             * and `regionTop` is the canvas's own top, i.e. the control band's height PLUS the
             * header above it. The rail's expression was missing that header term, so it was
             * taller than the maximized card by roughly the page title's height, every time.
             *
             * ⚠️ THAT TERM IS NO LONGER MISSING — the expression reads `pinnedChromeHeight`
             * (title band + control band) since the title was pinned, so the two sides now
             * agree. It is NOT a reason to drop the zero below: `pinnedChromeHeight` still
             * omits the hero, which is exactly the ~170px that unmounts when a card expands,
             * so the rail would overshoot again by that much in the state that matters. Zero
             * is the fix; the corrected term merely stops the non-expanded case being wrong
             * for a second, unrelated reason.
             *
             * Measured at 1280×800, "Scans by time" maximized, with the FLIP neutralised so
             * the settled layout could be read:
             *
             *   expanded card ......  552.5px   (224 → 776.5)
             *   rail ...............  609px     ← width 0, `MuiCollapse-hidden`, invisible
             *   row ................  609px
             *   scrollHeight .......  857px  against clientHeight 800
             *
             * So a state whose entire design is "fill the viewport exactly, nothing scrolls"
             * shipped with 57px of scroll room behind an element the reader cannot see. Any
             * nudge — a wheel tick, scroll anchoring as the FLIP's transform inflates and
             * deflates the scroll area, a focus landing low — scrolls the page, and because
             * the header was NOT sticky at the time (only the control block was), the first
             * thing to leave was the page title, with the date row clipped against the top
             * edge behind it. Reproduced by setting `scrollTop` directly: at 40 the title was
             * cut mid-height, at 57 it was gone entirely.
             *
             * The title pins now, so that particular symptom cannot recur — but the CAUSE is
             * still the rail's height, and hiding a symptom is not fixing it. Keep the zero:
             * a page that can scroll in a state designed not to will find something else to
             * move.
             *
             * ⚠️ WHY THE FIX IS HERE AND NOT ON `STICKY_BLEED_PX`. The bleed is the obvious
             * suspect — an opaque band painted 24px above an element is exactly the shape of
             * "something covers the top" — and it is innocent. `elementFromPoint` over the
             * date field in the maximized state returns the INPUT at every row of its own
             * box; the band only ever paints the strip above the sticky block, which is what
             * it is for. Nothing is painting over anything. The content is genuinely
             * scrolled. Do not touch that constant; it is pinned from both sides by
             * measurement and 24 is exact.
             *
             * Zero rather than `auto`: `auto` on a `flex-start` item resolves to its
             * content's height, and the panel's content is a full card library — taller than
             * the rail, so it would make the overflow worse. Zero, with `in` false (above),
             * takes the rail out of the row's height calculation entirely and the page
             * returns to exactly one viewport.
             */
            height: expandedId ? 0 : vhMinus(PANEL_VIEWPORT_RESERVE_PX + pinnedChromeHeight),
            // The panel fills the rail; the chain has to be unbroken or `height: 100%` on
            // the panel resolves against an auto-height box and collapses to its content.
            '& .MuiCollapse-wrapper, & .MuiCollapse-wrapperInner': { height: '100%' },
          }}
        >
          <AnalyticsCustomizePanel
            cards={cards}
            cardIds={view.cardIds}
            onAdd={view.addCard}
            onRemove={view.removeCard}
            onDragCardChange={setDroppingCardId}
            // Restores the ACTIVE view's cards + geometry, and NOT the filters — a
            // different promise from the Views menu's "Reset to default", which clears
            // the active view as well. See `useAnalyticsView.resetLayout`.
            onResetLayout={view.resetLayout}
            onClose={() => setCustomizeOpen(false)}
            // The row's gutter, carried by the panel so it collapses with it (see the
            // Stack's `spacing={0}` note). Pinning is the RAIL's job now — see the
            // `Collapse` sx above for why it cannot be the panel's.
            sx={{ ml: 6 }}
          />
        </Collapse>
      </Stack>
    </Box>
  )
}
